import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Popover, Progress, Tag, Typography, message } from 'antd';
import {
  CheckCircleOutlined,
  ClearOutlined,
  CloseOutlined,
  CopyOutlined,
  InfoCircleOutlined,
  PushpinOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { makeStyles, useMediaQuery } from '@material-ui/core';
import {
  HOLE_NUMBERS,
  HOLE_SLOTS,
  SLOT_LETTERS,
  assignmentsFromPlayers,
  groupIdsBySlot,
  assignGroup,
  unassignGroup,
  autoFillAssignments,
  changedAssignments,
} from '../../../utilities';
import { adminCard, adminColors, SITE_FOOTER_CLEARANCE, SITE_NAV_HEIGHT } from './adminStyles';

const { Text } = Typography;

const TRAY_TOP = SITE_NAV_HEIGHT + 16;

const useStyles = makeStyles((theme) => ({
  toolbar: {
    ...adminCard,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
    padding: '12px 16px',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      marginBottom: 12,
      padding: 12,
    },
  },
  progress: {
    width: 240,
    marginRight: 'auto',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginRight: 0,
    },
  },
  toolbarButton: {
    [theme.breakpoints.down('sm')]: {
      flex: 1,
    },
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '240px minmax(0, 1fr)',
    gap: 16,
    alignItems: 'start',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: 12,
    },
  },
  tray: {
    ...adminCard,
    position: 'sticky',
    top: TRAY_TOP,
    maxHeight: `calc(100vh - ${TRAY_TOP + SITE_FOOTER_CLEARANCE}px)`,
    overflowY: 'auto',
    padding: 12,
    [theme.breakpoints.down('sm')]: {
      // Pinned under the site nav as a sideways strip, so groups stay in reach while scrolling the holes.
      top: SITE_NAV_HEIGHT,
      zIndex: 5,
      maxHeight: 'none',
      overflowY: 'visible',
      padding: 10,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    },
  },
  trayList: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      gap: 8,
      margin: '0 -10px',
      padding: '0 10px 2px',
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      '& $groupCard': {
        flex: '0 0 auto',
        maxWidth: '75%',
        marginBottom: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      },
    },
  },
  trayDropTarget: {
    borderColor: '#1890ff',
    backgroundColor: '#e6f7ff',
  },
  trayHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  hint: {
    display: 'block',
    marginTop: 8,
    fontSize: 12,
  },
  // Touch screens can't hover, so they always show the buttons that hovering reveals.
  infoButton: {
    visibility: 'hidden',
    '@media (hover: none)': {
      visibility: 'visible',
    },
  },
  pinnedInfoButton: {
    visibility: 'visible',
    color: '#1890ff',
  },
  removeButton: {
    visibility: 'hidden',
    '@media (hover: none)': {
      visibility: 'visible',
    },
  },
  groupCard: {
    position: 'relative',
    marginBottom: 8,
    padding: '8px 30px 8px 10px',
    lineHeight: 1.4,
    backgroundColor: '#fff',
    border: '1px solid #d9d9d9',
    borderRadius: 6,
    cursor: 'grab',
    userSelect: 'none',
    '&:hover': {
      borderColor: '#1890ff',
    },
    '&:hover $infoButton': {
      visibility: 'visible',
    },
    '& $infoButton, & $pinnedInfoButton': {
      position: 'absolute',
      top: 4,
      right: 4,
    },
  },
  selected: {
    borderColor: '#1890ff',
    backgroundColor: '#e6f7ff',
    boxShadow: '0 0 0 1px #1890ff',
  },
  board: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: 12,
  },
  hole: {
    ...adminCard,
    padding: 10,
  },
  holeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  slot: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    minHeight: 34,
    marginTop: 6,
    padding: '2px 2px 2px 8px',
    fontSize: 13,
    border: '1px dashed #d9d9d9',
    borderRadius: 6,
    cursor: 'pointer',
    '@media (pointer: coarse)': {
      minHeight: 40,
    },
    userSelect: 'none',
    '&:hover $infoButton, &:hover $removeButton': {
      visibility: 'visible',
    },
  },
  filledSlot: {
    borderStyle: 'solid',
    borderColor: '#f0f0f0',
    backgroundColor: '#fafafa',
    cursor: 'grab',
    '&:hover': {
      borderColor: '#1890ff',
    },
  },
  openSlot: {
    borderColor: '#1890ff',
    backgroundColor: '#e6f7ff',
  },
  freshSlot: {
    borderColor: '#52c41a',
  },
  dropTarget: {
    borderStyle: 'solid',
    borderColor: '#1890ff',
    backgroundColor: '#bae7ff',
  },
  slotLetter: {
    width: 14,
    fontWeight: 600,
    color: '#8c8c8c',
  },
  slotNames: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  saveBar: {
    position: 'sticky',
    bottom: SITE_FOOTER_CLEARANCE,
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 16,
    padding: '10px 16px',
    backgroundColor: '#fffbe6',
    border: '1px solid #ffe58f',
    borderRadius: 8,
  },
  details: {
    width: 300,
  },
  detailsTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    padding: '4px 0',
  },
  detailRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '8px 0',
    borderBottom: `1px solid ${adminColors.border}`,
  },
  detailText: {
    flex: 1,
    minWidth: 0,
    '& .ant-typography': {
      display: 'block',
    },
  },
  copyAll: {
    width: '100%',
    marginTop: 10,
  },
}));

const classes = (...names) => names.filter(Boolean).join(' ');

const lastName = (name = '') => name.trim().split(/\s+/).pop();

function copyEmails(players) {
  const emails = players.map(player => player.email).join(', ');

  navigator.clipboard.writeText(emails).then(
    () => message.success(`Copied ${players.length} email${players.length === 1 ? '' : 's'}`),
    () => message.error("Couldn't copy the emails")
  );
}

// Names, emails, and shirt sizes for one group. Shown on hover, or pinned open.
function GroupPopover({ group, slot, pinned, dragging, onClosePin, children }) {
  const styles = useStyles();
  const [hovered, setHovered] = useState(false);
  // A tap fires mouseenter on touch screens, so only real hovers open the popover there.
  const canHover = useMediaQuery('(hover: hover)');
  const { players } = group;

  const title = (
    <div className={styles.detailsTitle}>
      <Text strong>
        {slot ? `Hole ${slot}` : 'Unassigned'} · {players.length} player{players.length === 1 ? '' : 's'}
      </Text>
      {pinned && (
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClosePin} aria-label="Close group details" />
      )}
    </div>
  );

  const content = (
    <div className={styles.details}>
      {players.map(player => (
        <div key={player._id} className={styles.detailRow}>
          <div className={styles.detailText}>
            <Text>{player.name}</Text>
            <Text type="secondary" copyable={{ text: player.email }} ellipsis>{player.email}</Text>
          </div>
          <Tag>{player.shirtSize || 'N/A'}</Tag>
        </div>
      ))}
      <Button size="small" icon={<CopyOutlined />} className={styles.copyAll} onClick={() => copyEmails(players)}>
        Copy all emails
      </Button>
    </div>
  );

  return (
    <Popover
      title={title}
      content={content}
      placement={canHover ? 'rightTop' : 'bottom'}
      trigger="hover"
      mouseEnterDelay={0.45}
      mouseLeaveDelay={0.15}
      visible={pinned || (canHover && hovered && !dragging)}
      onVisibleChange={setHovered}
    >
      {children}
    </Popover>
  );
}

export default function HoleAssignmentBoard({ players, onSaved, onDirtyChange }) {
  const styles = useStyles();
  const canHover = useMediaQuery('(hover: hover)');

  const groups = useMemo(() => {
    const playersByGroup = {};

    players.forEach(player => {
      playersByGroup[player.groupId] = playersByGroup[player.groupId] || [];
      playersByGroup[player.groupId].push(player);
    });

    return Object.entries(playersByGroup)
      .map(([groupId, groupPlayers]) => ({
        groupId,
        players: groupPlayers,
        label: groupPlayers.map(player => lastName(player.name)).join(' · '),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [players]);

  const groupsById = useMemo(() => {
    const byId = {};
    groups.forEach(group => { byId[group.groupId] = group; });
    return byId;
  }, [groups]);

  const saved = useMemo(() => assignmentsFromPlayers(players), [players]);

  const [draft, setDraft] = useState(saved);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [draggingGroupId, setDraggingGroupId] = useState(null);
  const [dropSlot, setDropSlot] = useState(null);
  const [trayIsDropTarget, setTrayIsDropTarget] = useState(false);
  const [freshSlots, setFreshSlots] = useState([]);
  const [pinnedGroupId, setPinnedGroupId] = useState(null);
  const [saving, setSaving] = useState(false);

  // A fresh roster (after saving) replaces the draft.
  useEffect(() => {
    setDraft(saved);
    setFreshSlots([]);
  }, [saved]);

  const changes = useMemo(() => changedAssignments(saved, draft), [saved, draft]);
  const changeCount = Object.keys(changes).length;

  useEffect(() => {
    onDirtyChange(changeCount > 0);
  }, [changeCount, onDirtyChange]);

  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  useEffect(() => {
    if (!changeCount) return undefined;

    const warnBeforeLeaving = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [changeCount]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedGroupId(null);
        setPinnedGroupId(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const slotOccupants = useMemo(() => groupIdsBySlot(draft), [draft]);
  const unassignedGroups = groups.filter(group => !draft[group.groupId]);
  const assignedCount = groups.length - unassignedGroups.length;
  const extraGroups = groups.length - HOLE_SLOTS.length;
  const movingGroupId = draggingGroupId || selectedGroupId;

  const placeGroup = (groupId, slot) => {
    setDraft(current => assignGroup(current, groupId, slot));
    setSelectedGroupId(null);
    setFreshSlots([slot]);
  };

  const removeGroup = (groupId) => {
    setDraft(current => unassignGroup(current, groupId));
    setSelectedGroupId(null);
    setFreshSlots([]);
  };

  const toggleSelected = (groupId) => {
    setSelectedGroupId(current => (current === groupId ? null : groupId));
  };

  const togglePinned = (groupId) => {
    setPinnedGroupId(current => (current === groupId ? null : groupId));
  };

  const handleAutoFill = () => {
    const result = autoFillAssignments(groups.map(group => group.groupId), draft);

    if (!result.filledSlots.length) {
      message.info(unassignedGroups.length ? 'Every slot is already taken.' : 'Every group already has a hole.');
      return;
    }

    setDraft(result.assignments);
    setFreshSlots(result.filledSlots);
    setSelectedGroupId(null);
  };

  const handleClearAll = () => {
    setDraft({});
    setFreshSlots([]);
    setSelectedGroupId(null);
  };

  const handleDiscard = () => {
    setDraft(saved);
    setFreshSlots([]);
    setSelectedGroupId(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch('/api/players/hole-assignments', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ assignments: changes }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to save hole assignments.');
      }

      message.success('Hole assignments saved');
      await onSaved();
    } catch (error) {
      message.error(error.message);
      console.error('Error saving hole assignments:', error);
    } finally {
      setSaving(false);
    }
  };

  const dragProps = (groupId) => ({
    draggable: true,
    onDragStart: (event) => {
      event.dataTransfer.setData('text/plain', groupId);
      event.dataTransfer.effectAllowed = 'move';
      setDraggingGroupId(groupId);
    },
    onDragEnd: () => {
      setDraggingGroupId(null);
      setDropSlot(null);
      setTrayIsDropTarget(false);
    },
  });

  const draggedGroupId = (event) => {
    const groupId = event.dataTransfer.getData('text/plain');
    return groupsById[groupId] ? groupId : null;
  };

  const leftTarget = (event) => !event.currentTarget.contains(event.relatedTarget);

  const renderInfoButton = (groupId) => {
    const pinned = pinnedGroupId === groupId;

    return (
      <Button
        type="text"
        size="small"
        icon={pinned ? <PushpinOutlined /> : <InfoCircleOutlined />}
        className={pinned ? styles.pinnedInfoButton : styles.infoButton}
        aria-label={pinned ? 'Unpin group details' : 'Pin group details'}
        onClick={(event) => {
          event.stopPropagation();
          togglePinned(groupId);
        }}
      />
    );
  };

  const renderGroupCard = (group) => (
    <GroupPopover
      key={group.groupId}
      group={group}
      slot={null}
      pinned={pinnedGroupId === group.groupId}
      dragging={!!draggingGroupId}
      onClosePin={() => setPinnedGroupId(null)}
    >
      <div
        className={classes(styles.groupCard, selectedGroupId === group.groupId && styles.selected)}
        onClick={() => toggleSelected(group.groupId)}
        {...dragProps(group.groupId)}
      >
        {group.label}
        {renderInfoButton(group.groupId)}
      </div>
    </GroupPopover>
  );

  const renderSlot = (slot, letter) => {
    const occupantId = slotOccupants[slot];
    const group = occupantId && groupsById[occupantId];

    const slotElement = (
      <div
        key={slot}
        className={classes(
          styles.slot,
          group && styles.filledSlot,
          !group && movingGroupId && styles.openSlot,
          freshSlots.includes(slot) && styles.freshSlot,
          group && selectedGroupId === occupantId && styles.selected,
          dropSlot === slot && styles.dropTarget
        )}
        onClick={() => {
          if (selectedGroupId && selectedGroupId !== occupantId) {
            placeGroup(selectedGroupId, slot);
          } else if (occupantId) {
            toggleSelected(occupantId);
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setDropSlot(slot);
        }}
        onDragLeave={(event) => {
          if (leftTarget(event)) setDropSlot(null);
        }}
        onDrop={(event) => {
          event.preventDefault();
          const groupId = draggedGroupId(event);
          if (groupId) placeGroup(groupId, slot);
          setDropSlot(null);
        }}
        {...(group ? dragProps(occupantId) : {})}
      >
        <span className={styles.slotLetter}>{letter}</span>
        {group ? (
          <>
            <span className={styles.slotNames}>{group.label}</span>
            {renderInfoButton(occupantId)}
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              className={styles.removeButton}
              aria-label={`Unassign ${group.label}`}
              onClick={(event) => {
                event.stopPropagation();
                removeGroup(occupantId);
              }}
            />
          </>
        ) : (
          <Text type="secondary" className={styles.slotNames}>Open</Text>
        )}
      </div>
    );

    if (!group) return slotElement;

    return (
      <GroupPopover
        key={slot}
        group={group}
        slot={slot}
        pinned={pinnedGroupId === occupantId}
        dragging={!!draggingGroupId}
        onClosePin={() => setPinnedGroupId(null)}
      >
        {slotElement}
      </GroupPopover>
    );
  };

  return (
    <div>
      <div className={styles.toolbar}>
        <div className={styles.progress}>
          <Text strong>{assignedCount} of {groups.length} groups assigned</Text>
          <Progress
            percent={groups.length ? Math.round((assignedCount / groups.length) * 100) : 0}
            showInfo={false}
            size="small"
          />
        </div>
        <Button icon={<ClearOutlined />} onClick={handleClearAll} disabled={!assignedCount} className={styles.toolbarButton}>
          Clear all
        </Button>
        <Button
          type="primary"
          icon={<ThunderboltOutlined />}
          onClick={handleAutoFill}
          disabled={!groups.length}
          className={styles.toolbarButton}
        >
          Auto-fill
        </Button>
      </div>

      {extraGroups > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`There are ${groups.length} groups but only ${HOLE_SLOTS.length} slots, so ${extraGroups} group${extraGroups === 1 ? '' : 's'} can't be placed.`}
        />
      )}

      <div className={styles.layout}>
        <div
          className={classes(styles.tray, trayIsDropTarget && styles.trayDropTarget)}
          onDragOver={(event) => {
            event.preventDefault();
            setTrayIsDropTarget(true);
          }}
          onDragLeave={(event) => {
            if (leftTarget(event)) setTrayIsDropTarget(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            const groupId = draggedGroupId(event);
            if (groupId) removeGroup(groupId);
            setTrayIsDropTarget(false);
          }}
        >
          <div className={styles.trayHeader}>
            <Text type="secondary">Unassigned</Text>
            <Text type="secondary">{unassignedGroups.length}</Text>
          </div>
          <div className={styles.trayList}>
            {unassignedGroups.length ? (
              unassignedGroups.map(renderGroupCard)
            ) : (
              <Text type="success"><CheckCircleOutlined /> Every group has a hole</Text>
            )}
          </div>
          <Text type="secondary" className={styles.hint}>
            {canHover
              ? 'Drag a group onto a slot, or click it and then click a slot. Hover a group for its details.'
              : 'Tap a group, then tap a slot.'}
          </Text>
        </div>

        <div className={styles.board}>
          {HOLE_NUMBERS.map(hole => {
            const groupCount = SLOT_LETTERS.filter(letter => slotOccupants[`${hole}${letter}`]).length;

            return (
              <div key={hole} className={styles.hole}>
                <div className={styles.holeHeader}>
                  <Text strong>Hole {hole}</Text>
                  <Text type={groupCount === SLOT_LETTERS.length ? 'success' : 'secondary'} style={{ fontSize: 12 }}>
                    {groupCount}/{SLOT_LETTERS.length}
                  </Text>
                </div>
                {SLOT_LETTERS.map(letter => renderSlot(`${hole}${letter}`, letter))}
              </div>
            );
          })}
        </div>
      </div>

      {changeCount > 0 && (
        <div className={styles.saveBar}>
          <Text style={{ flex: 1 }}>
            {changeCount} unsaved change{changeCount === 1 ? '' : 's'}
          </Text>
          <Button onClick={handleDiscard} disabled={saving}>Discard</Button>
          <Button type="primary" onClick={handleSave} loading={saving}>Save</Button>
        </div>
      )}
    </div>
  );
}
